<script lang="ts" setup>
import {
  computed,
  defineOgImageComponent,
  onMounted,
  ref,
  useUser,
  useDialog,
  useHead,
  useMetrics,
  useRoute,
  useTemplateRef,
  useRouter,
  useAuth,
  useStorage,
  useToast,
  useMaps,
  callOnce,
  useSpots,
  watch,
  useCreators,
  useFollowers,
  reactive,
  useBreadcrumbs,
  waitMs,
} from "#imports";
import MPShare from "~/components/dialogs/MPShare.vue";
import MPPricing from "~/components/dialogs/MPPricing.vue";
import MPJoinMessage from "~/components/dialogs/MPJoinMessage.vue";
import MPFollowCreator from "~/components/dialogs/MPFollowCreator.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPCreatorTag from "~/components/widgets/MPCreatorTag.vue";
import MPBookmark from "~/components/widgets/MPBookmark.vue";
import MPViewportSpill from "~/components/layouts/MPViewportSpill.vue";
import MPCategoryFilter from "~/components/forms/MPCategoryFilter.vue";
import MPMap from "~/components/layouts/MPMap.client.vue";
import MPSpotCard from "~/components/layouts/MPSpotCard.vue";
import MPAuth from "~/components/dialogs/MPAuth/MPAuth.vue";
import { useBreakpoints, useScroll, useScrollLock } from "@vueuse/core";
import { type UserPaymentInterval, type UserTier } from "~/stores/user.ts";
import type { CommonGestureState } from "@vueuse/gesture";
import type { Spot } from "~/stores/spots";

const route = useRoute();
const router = useRouter();
const spotStore = useSpots();
const mapStore = useMaps();
const creatorStore = useCreators();
const userStore = useUser();
const authStore = useAuth();
const { backLabel, backLink } = useBreadcrumbs();
const { showError } = useToast();
const { openDialog, setDialogFullscreen } = useDialog();
const { formattedImageUrl } = useStorage();
const { trackEvent } = useMetrics();
const followStore = useFollowers();

// Workaround to grab the body element which is the scrolling element we track
const bodyEl = ref<HTMLElement | null>(null);
onMounted(() => {
  bodyEl.value = document.body;
});
const creatorTagY = useScroll(bodyEl).y;
const showCreatorTag = computed(() => creatorTagY.value > 90);

const data = computed(() => {
  const map = mapStore.mapBySlugOrId!;

  return {
    ...map,
    spots: spotStore.getByIds(map?.spotIds ?? []).value!,
    creator: creatorStore.getById(map?.user_id).value!,
  };
});
const MAP_VIEW_LIMIT = 2;
const viewMapCount = (
  creatorId: string,
  creatorHandle: string,
  creatorImage: string,
) => {
  // If prev route is not discover/explore return?
  if (followStore.followers.length > 0) return;

  followStore.viewedMapCount++;

  if (followStore.viewedMapCount % MAP_VIEW_LIMIT === 0) {
    if (!userStore.user.id) {
      openDialog(MPAuth, { startOn: "signup" });
    } else {
      trackEvent("follow-this-creator-dialog", {
        creator_id: data.value.creator.id,
        map_id: data.value.id,
      });
      openDialog(
        MPFollowCreator,
        {
          creatorId,
          creatorHandle,
          creatorImage,
        },
        {
          closeBtnColor: "var(--color-indigo-400)",
        },
      );
    }
  }
};

const loadData = async (refetch: boolean = false) => {
  if (typeof route.params.mapId !== "string") {
    return;
  }
  try {
    const map = await mapStore.fetchBySlugOrId(route.params.mapId, refetch);
    await spotStore.fetchByIds(map.value!.spotIds);
    await creatorStore.fetchById(map.value!.user_id);

    const creator = creatorStore.getById(map.value!.user_id);

    viewMapCount(
      map.value!.user_id,
      creator.value!.mappr_handle!,
      creator.value!.profile_image_url ?? "/media/images/placeholder-user.webp",
    );
  } catch (e) {
    showError("Map does not exist", e);
  }
};

await callOnce(loadData, { mode: "navigation" });
watch(
  () => route.params.mapId,
  () => loadData(),
);
watch(
  () => authStore.isLoggedIn,
  () => loadData(true),
);

// Check User properties
const userIsMapAuthor = computed(() => {
  if (!userStore.user?.id) return false;
  return userStore.user.id === data.value.creator?.id;
});

const userIsSupporter = computed(
  () => userStore.isSupporter(data.value.creator?.id ?? "").value,
);

const breakpoints = useBreakpoints({
  mobile: 920,
});
const isMobile = breakpoints.smaller("mobile");

const handleBackButton = async () => {
  if (backLink) {
    router.push(backLink);
  } else {
    router.push("/explore");
  }
};

// Setup SEO
useHead(() => ({
  title: data.value.title,
  meta: [
    {
      name: "description",
      content: data.value.summary,
    },
  ],
}));

defineOgImageComponent(
  "Default",
  {
    title: data.value.title,
    description: data.value.summary,
    by: `Map By: @${data.value.creator?.mappr_handle}`,
  },
  {
    url: formattedImageUrl(data.value.cover_image_url!, 1200, 600, 75),
  },
);

const backgroundImage = computed(
  () =>
    `url(${formattedImageUrl(data.value.cover_image_url!, 2000, 1000, 100)})`,
);

const isFullscreen = ref(true);

const openShareDialog = () => {
  try {
    openDialog(MPShare, {
      id: data.value.id,
      version: "map",
    });
  } catch (e) {
    showError("Error opening share dialog", e);
  }
};

const paymentInterval = ref<UserPaymentInterval>("month");
const pricingTier = ref<UserTier>("premium");
const openPricingDialog = (
  tier: UserTier = "premium",
  interval?: UserPaymentInterval,
) => {
  pricingTier.value = tier;
  openDialog(MPPricing, {
    creatorId: data.value.creator.id,
    showSupportTier: true,
    intervalProp: interval,
  });
  setDialogFullscreen(true);
  trackEvent(`${tier}_intent`, {
    map_id: data.value.id,
    map_name: data.value.title,
    creator_id: data.value.creator?.mappr_handle,
    creator_handle: data.value.creator?.mappr_handle,
  });
};

const scrollContainer = useTemplateRef("scrollContainer");
const spotListCurrentIndex = ref<number>(0);
const isAnimating = ref(false);
const spotListDragHandler = async (args: CommonGestureState) => {
  if (!isMobile.value) return;
  if (!scrollContainer.value || isAnimating.value) return;
  const listItems = scrollContainer.value.querySelectorAll("li");

  let newIndex = spotListCurrentIndex.value;
  if (args.direction[1] > 0) {
    newIndex = Math.max(0, spotListCurrentIndex.value - 1);
  } else if (args.direction[1] < 0) {
    newIndex = Math.min(listItems.length - 1, spotListCurrentIndex.value + 1);
  }
  if (newIndex === spotListCurrentIndex.value) return;
  isAnimating.value = true;
  spotListCurrentIndex.value = newIndex;

  requestAnimationFrame(() => {
    listItems.forEach((item) => item.classList.remove("active"));
    listItems.forEach((item) => item.classList.remove("next"));
    listItems[spotListCurrentIndex.value]?.classList.add("active");
    listItems[spotListCurrentIndex.value + 1]?.classList.add("next");
  });

  await waitMs(300);
  isAnimating.value = false;
};

const { y } = useScroll(scrollContainer);
watch(y, () => {
  if (isMobile.value) return;
  const listItems = scrollContainer.value!.querySelectorAll("li");
  // set items visible and -1 for the callout and - 1 to start progress at 1
  const visibleListItems = listItems.length - 2;
  const scrollRatio =
    y.value /
    (scrollContainer.value!.scrollHeight - scrollContainer.value!.clientHeight);
  const progress = scrollRatio * visibleListItems;

  const filteredIndex = Math.min(Math.floor(progress), visibleListItems);
  spotListCurrentIndex.value = filteredIndex;
});

const mapComponent = useTemplateRef("mapComponent");
watch(spotListCurrentIndex, () => {
  if (!isMobile.value) return;
  const currentSpot = filteredSpots.value[spotListCurrentIndex.value];
  if (!currentSpot) return;
  mapComponent.value?.selectSpot(currentSpot.id);
});

onMounted(() => {
  trackEvent("viewed_map", {
    map_id: data.value.id,
    map_name: data.value.title,
    creator_handle: data.value.creator?.mappr_handle,
    creator_id: data.value.user_id,
  });
});

const selectedCategoryIds = reactive<string[]>([]);

// ideally this should be a v-model, but there's too much to fix elsewhere
const handleCategoryFilter = (categoryId: string) => {
  const idx = selectedCategoryIds.findIndex((id) => id === categoryId);
  if (idx === -1) {
    selectedCategoryIds.push(categoryId);
  } else {
    selectedCategoryIds.splice(idx, 1);
  }
};

const spotVisibilityParams = {
  public: {
    label: "🏞 Free Spots",
    order: 1,
  },
  authenticated: {
    label: "✨ Member Spots",
    order: 2,
  },
  subscriber: {
    label: "✨ Premium Spots",
    order: 3,
  },
  supporter: {
    label: "✨ Supporter Spots",
    order: 4,
  },
};

const filteredSpots = computed(() => {
  if (!data.value.spots.length) {
    return [];
  }

  let spots = data.value.spots;
  if (selectedCategoryIds.length > 0) {
    spots = data.value.spots.filter((spot) =>
      selectedCategoryIds.includes(spot.category_id!),
    );
  }

  spots.sort((a, b) => {
    const orderA = spotVisibilityParams[a.visibility ?? "public"].order;
    const orderB = spotVisibilityParams[b.visibility ?? "public"].order;
    return orderA - orderB;
  });

  return spots;
});

const isSpotOpen = computed(() => route.params.spotId !== undefined);
const openSpot = (spotId: string) => {
  const spot = data.value.spots.find((s) => s.id === spotId)!;
  router.replace(
    `/maps/${data.value.current_slug}/spots/${spot.current_slug}#no-scroll`,
  );
};

const closeSpot = () => {
  router.replace(`/maps/${data.value.current_slug}#no-scroll`);
};

// not really spotId, but spotSlug!
watch(
  () => route.params.spotId,
  (newVal, oldValue) => {
    if (newVal === oldValue) {
      return;
    }
    if (typeof route.params.spotId !== "string") {
      return;
    }
    const spot = data.value.spots.find(
      (s) => s.current_slug === route.params.spotId,
    );
    mapComponent.value?.selectSpot(spot?.id ?? null);
  },
);

watch(mapComponent, () => {
  // when map is loaded, select the current spot
  if (typeof route.params.spotId !== "string") {
    return;
  }
  const spot = data.value.spots.find(
    (s) => s.current_slug === route.params.spotId,
  );
  mapComponent.value?.selectSpot(spot?.id ?? null);
});

const spotFullscreen = ref<boolean>(false);

const progressPercentage = computed(() => {
  if (!data.value || data.value.spots_count === 0) {
    return 0;
  }

  return Math.round(
    (spotListCurrentIndex.value / data.value.spots_count!) * 100,
  );
});

const showCategories = ref<boolean>(false);

const isVisible = (spot: Spot) =>
  computed<boolean>(() => {
    if (spot.visibility === "public") {
      return true;
    } else if (spot.visibility === "authenticated") {
      return !!userStore.user.id;
    } else if (
      spot.visibility === "subscriber" ||
      spot.visibility === "supporter"
    ) {
      return userStore.isPremium || userStore.isAdmin || userIsMapAuthor.value;
    }
    return false;
  });

const spotView = useTemplateRef("spotView");
const isLocked = useScrollLock(spotView);
const handlePreviewOverlay = (event: Event) => {
  if (event) {
    isLocked.value = true;
  } else {
    isLocked.value = false;
  }
};
</script>

<template>
  <main
    ref="mapPageRef"
    :class="[
      'map-page',
      { 'is-fullscreen': isFullscreen, 'has-open-spot': isSpotOpen },
    ]"
    v-if="data?.id"
  >
    <div class="header" ref="mapHeader">
      <div class="header-content">
        <div class="navigation-breadcrumb">
          <i class="ri-arrow-left-s-line" />
          <NuxtLink v-if="backLink && backLabel" :to="backLink">{{
            backLabel
          }}</NuxtLink>
          <NuxtLink v-else to="/explore">Back to Explore</NuxtLink>
        </div>
        <MPButton
          icon="ri-arrow-left-s-line"
          size="large"
          hideLabel
          label="Back"
          class="back-button"
          version="secondary"
          rounded
          @click="handleBackButton"
        />
        <h1 class="title">
          {{ data.title }}
        </h1>
        <p class="summary typeface-summary">
          {{ data.summary }}
        </p>
        <MPCreatorTag :creatorId="data.creator.id" />

        <Transition name="fade">
          <MPCreatorTag
            v-show="showCreatorTag"
            :creatorId="data.creator.id"
            class="creator-tag"
          />
        </Transition>
      </div>
    </div>
    <div class="map-view" id="map-view" ref="mapView">
      <MPMap
        ref="mapComponent"
        :spotIds="filteredSpots.map((s) => s.id)"
        :gestureHandling="isMobile ? 'greedy' : 'cooperative'"
        @spotClick="openSpot"
        :padding="{ top: 16, right: 16, bottom: 16, left: 16 }"
      />
    </div>
    <div class="spots-list">
      <ul ref="scrollContainer" v-drag="spotListDragHandler">
        <li class="swipe-instruction active">
          <img src="/images/swipe-up.svg" alt="Swipe up" />
          Swipe up to navigate spots
        </li>
        <template v-for="(spot, idx) in filteredSpots" :key="spot.id">
          <li v-if="isVisible(spot)" :class="{ active: idx === 0 }">
            <MPSpotCard showTier :spotId="spot.id" version="small" actions />
          </li>
        </template>
        <li class="callout" v-if="!authStore.isLoggedIn">
          <div class="auth-callout">
            <h5 class="typeface-summary">See more</h5>
            <p class="typeface-small">
              Join to explore, and save handpicked spots collected by creators
              you trust.
            </p>
            <MPButton
              label="Create free account"
              version="secondary"
              iconColor="var(--color-rose-500)"
              backgroundColor="var(--color-white)"
              @click="openDialog(MPJoinMessage, {})"
            />
          </div>
        </li>
        <li
          class="callout"
          v-else-if="
            authStore.isLoggedIn &&
            !userStore.isPremium &&
            !userIsMapAuthor &&
            !userStore.isAdmin
          "
        >
          <article class="premium-callout typeface-regular">
            <div class="premium-callout-content">
              <h5>Want to see more.</h5>
              <p>Sign up for premium and unlock exclusive Spots</p>
              <div class="premium-callout-button-container">
                <MPButton
                  label="Go Premium"
                  version="info"
                  backgroundColor="var(--color-white)"
                  iconColor="var(--color-indigo-600)"
                  @click="openPricingDialog('premium', paymentInterval)"
                />
                <small>Cancel&nbsp;anytime</small>
              </div>
            </div>
          </article>
        </li>
        <li
          class="callout"
          v-else-if="
            authStore.isLoggedIn &&
            !userIsSupporter &&
            !userIsMapAuthor &&
            !userStore.isAdmin
          "
        >
          <article class="support-callout">
            <div class="support-content">
              <h5>Unlock exclusive spots.</h5>
              <p>
                Support your favorite creators. Your contribution goes straight
                to them.
              </p>
              <div class="support-callout-button-container">
                <MPButton
                  label="Support this creator"
                  version="info"
                  backgroundColor="var(--color-white)"
                  iconColor="var(--color-slate-700)"
                  @click="openPricingDialog('supporter', paymentInterval)"
                />
              </div>
            </div>
          </article>
        </li>
        <li v-else class="swipe-instruction">
          <img src="/images/swipe-down.svg" alt="Swipe down" />
          Swipe down to go back
        </li>
      </ul>
    </div>
    <div
      class="spot-view"
      ref="spotView"
      :class="{ 'is-spot-fullscreen': spotFullscreen }"
    >
      <NuxtPage
        :scrollingContainer="spotView"
        @setFullScreen="spotFullscreen = $event"
        @close="closeSpot"
        @subscribe="openPricingDialog()"
        @preview="handlePreviewOverlay($event)"
      />
    </div>
    <div class="filter" v-if="showCategories">
      <MPViewportSpill showInlineControl>
        <MPCategoryFilter
          :categories="data.categories"
          :selectedFromFilter="selectedCategoryIds"
          @click="handleCategoryFilter"
        />
      </MPViewportSpill>
    </div>
    <div class="controls" ref="controls">
      <MPButton
        label="Filters"
        icon="ri-equalizer-2-line"
        version="secondary"
        size="medium"
        rounded
        :hideLabel="true"
        @click="showCategories = !showCategories"
      />
      <MPButton
        label="Share Map"
        icon="ri-share-line"
        version="secondary"
        size="medium"
        rounded
        :hideLabel="true"
        @click="openShareDialog()"
      />
      <MPBookmark
        bookmark="map_bookmarks"
        :id="data.id"
        version="secondary"
        :hideLabel="true"
        rounded
      />
      <MPButton
        label="Subscribe"
        icon="ri-lock-2-line"
        version="secondary"
        size="medium"
        rounded
        :hideLabel="true"
        @click="openPricingDialog()"
      />
    </div>
    <div class="progress-container">
      <div class="progress-text">
        <span>Free&nbsp;Spots&nbsp;</span>
      </div>
      <div class="progress-bar-container">
        <svg
          width="34"
          height="34"
          viewBox="0 0 34 34"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          style="transform: rotate(-90deg)"
        >
          <circle
            r="15"
            cx="17"
            cy="17"
            fill="transparent"
            stroke="#e0e0e0"
            stroke-width="2"
          />
          <circle
            r="15"
            cx="17"
            cy="17"
            stroke="var(--color-rose-400)"
            stroke-width="2"
            stroke-linecap="round"
            :stroke-dashoffset="`${(progressPercentage - 100) * -1}px`"
            fill="transparent"
            stroke-dasharray="100"
            style="transition: stroke-dashoffset 0.1s ease"
          />
          <text
            x="17"
            y="-16"
            fill="var(--color-slate-500)"
            font-size="10px"
            font-weight="bold"
            style="transform: rotate(90deg)"
            text
            text-anchor="middle"
            dominant-baseline="middle"
          >
            {{ spotListCurrentIndex + 1 }}/{{ data.spots_count }}
          </text>
        </svg>
      </div>
    </div>
  </main>
</template>

<style lang="postcss" scoped>
@import "assets/css/layouts.pcss";
@import "assets/css/breakpoints.pcss";
@import "assets/css/scrollbar.pcss";
.map-page {
  position: absolute;
  z-index: 8100;
  display: grid;
  grid-template-columns: auto;
  @mixin screen md {
    position: relative;
    z-index: 1;
    grid-template-rows:
      minmax(40rem, 60vh)
      calc(100vh - 3rem);
    grid-template-columns: 28rem 1fr;
    grid-template-areas:
      "header header"
      "spotlist mapview";
  }
}

.header {
  grid-area: header;
  background-color: white;
  padding: 0 0.5rem;
  z-index: 1;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 100vw;
  @mixin screen md {
    position: relative;
    display: grid;
    align-items: center;
    color: var(--color-slate-50);
    background-color: var(--color-slate-800);
    background-image: v-bind(backgroundImage);
    background-attachment: fixed;
    background-image:
      linear-gradient(
        320deg,
        rgba(0, 0, 0, 0) 30%,
        rgba(0, 0, 0, 0.6) 60%,
        rgba(0, 0, 0, 0.8) 100%
      ),
      v-bind(backgroundImage);
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  .navigation-breadcrumb {
    display: none;
    @mixin screen md {
      display: flex !important;
      gap: 0.5rem;
      background-color: rgba(164, 164, 164, 0.5);
      color: var(--color-slate-50);
      padding: 0.75rem 1rem 0.75rem 0.75rem;
      border-radius: 0.5rem;
    }
  }
  .back-button {
    font-size: 2rem;
    margin: 0.5rem 0;
    @mixin screen md {
      display: none !important;
    }
  }
  .header-content {
    display: flex;
    align-items: center;
    @mixin screen md {
      flex-direction: column;
      align-items: center;
      justify-content: start;
      @mixin layout-wrapper-wide;
    }
    .title {
      text-align: center;
      max-width: 50rem;

      @mixin screen-down md {
        font-weight: 400;
        margin: 0.5rem;
        text-align: left;
        max-width: inherit;
        margin-left: 1rem;
        font-size: 1.25rem;
      }
    }
    .summary {
      display: none;
      @mixin screen md {
        text-align: center;
        max-width: 36rem;
        display: block;
        margin: 0 0 2rem 0;
      }
    }
    .creator-tag {
      display: none;
      @mixin screen md {
        display: flex;
        background: white;
        box-shadow: var(--shadow-base);
        padding: 0.35rem;
        color: var(--color-slate-700);
        border-radius: 4rem;
      }
    }
  }
}

.controls {
  grid-area: controls;
  left: 0;
  gap: 0.5rem;
  position: fixed;
  width: 100%;
  bottom: 11.95rem;
  left: 0.75rem;
  z-index: 1;
  display: inline-flex;
  overflow-x: scroll;
  @mixin scrollbar-reset;
  @mixin screen md {
    grid-area: mapview;
    position: absolute;
    top: 1rem;
    right: 20rem;
    display: flex;
    flex-direction: column;
    width: fit-content;
    z-index: 1;
  }
  @mixin screen xxl {
    padding-left: calc((100vw - $layoutWide) / 2);
    transition: padding-left 150ms ease-in-out;
  }

  &:deep(button) {
    background: white !important;
    border: 0 !important;
    box-shadow: var(--shadow-base);
  }
}

.filter {
  position: fixed;
  left: 0;
  bottom: 15rem;
  :deep(.swipe-content ul) {
    padding: 0 0 0 0.75rem;
  }
  @mixin screen md {
    grid-area: mapview;
    position: absolute;
    top: 1rem;
    left: 3rem;
    height: fit-content;

    @mixin screen xxl {
      margin-left: calc(((100vw - $layoutWide) / 2));
      margin-right: calc(((100vw - $layoutWide) / 2));
      transition:
        margin-left 150ms ease-in-out,
        margin-right 150ms ease-in-out;
      :deep(.swipe-content ul) {
        padding: 0 0 0 0.75rem;
      }
    }
  }
}

.progress-container {
  position: fixed;
  bottom: 12rem;
  right: 0.5rem;
  background: white;
  border-radius: 2rem;
  padding: 0 0.2rem 0 0.75rem;
  z-index: 2;
  display: flex;
  align-items: center;
  box-shadow: var(--shadow-base);
  color: var(--color-slate-500);
  @mixin screen md {
    z-index: 3;
    grid-area: spotlist;
    position: relative;
    top: 1rem;
    left: 0.75rem;
    width: 26.5rem;
    padding-left: 1rem;
    height: fit-content;
    justify-content: space-between;
  }

  @mixin screen xxl {
    left: 0.5rem;
    margin-left: calc(((100vw - $layoutWide) / 2) + 0.75rem);
    transition: margin-left 150ms ease-in-out;
  }

  .progress-bar-container {
    width: fit-content;
    position: relative;
    top: 2px;
    margin-left: 0.25rem;
  }

  .progress-bar-value {
    height: 100%;
    background-color: var(--color-indigo-400);
    border-radius: 4px;
    transition: width 0.3s ease;
  }
}

.map-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  grid-area: mapview;
  @mixin screen md {
    position: relative;
    width: inherit;
    height: inherit;
    overflow: hidden;
  }
  @mixin screen xxl {
    padding-right: calc((100vw - $layoutWide) / 2);
    padding-left: calc((100vw - $layoutWide) / 2);
    transition:
      padding-right 150ms ease-in-out,
      padding-left 150ms ease-in-out;
  }
}

.spot-view {
  grid-area: mapview;
  display: block;
  max-width: 100vw;
  max-height: 100vh;
  overflow: scroll;
  z-index: 1000;
  @mixin screen md {
    z-index: 2;
    max-height: calc(100vh - 5rem);
    max-width: 30rem;
    position: absolute;
    top: 1rem;
    left: 1rem;
    border-radius: 1rem;
    overflow-x: hidden;
    @mixin scrollbar-reset;
    box-shadow: var(--shadow-md);
  }
  @mixin screen xxl {
    left: 0;
    margin-left: calc(((100vw - $layoutWide) / 2) + 1rem);
    margin-right: calc(((100vw - $layoutWide) / 2));
    transition:
      margin-left 150ms ease-in-out,
      margin-right 150ms ease-in-out;
    /*border-radius: 2rem;*/
  }
}

.spots-list {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  z-index: 3;
  grid-area: spotlist;
  @mixin screen md {
    background-color: white;
    position: relative;
    width: inherit;
    height: inherit;
    max-height: 100vh;
    padding: 3.25rem 1rem 0;
  }
  @mixin screen xxl {
    margin-left: calc((100vw - $layoutWide) / 2);
    transition: margin-left 150ms ease-in-out;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    padding: 1rem 0 0;
    margin: 0;
    height: 12rem;
    width: 100%;
    overflow-y: hidden;
    @mixin scrollbar-reset;
    @mixin screen md {
      width: 100%;
      gap: 1rem;
      height: 100%;
      overflow-y: scroll;
      @mixin scrollbar-reset;
    }
    li {
      background-color: white;
      padding: 0.5rem;
      border-radius: 1.5rem;
      @mixin screen-down md {
        position: absolute;
        top: 12rem;
        left: 50%;
        transform: translateX(-50%);
        margin: 0.75rem 0 0;
        width: 100%;
        max-width: calc(100vw - 1.5rem);
        z-index: 0;
        opacity: 0;
        animation-duration: 250ms;
        animation-name: list-item-out;
        :deep(img) {
          max-height: 9rem;
        }
      }
    }
    li.active {
      @mixin screen-down md {
        position: absolute;
        top: 0;
        animation-duration: 250ms;
        animation-name: list-item-in;
        box-shadow: var(--shadow-md);
        scale: 1;
        z-index: 2;
        opacity: 1;
      }
    }
    li.next {
      @mixin screen-down md {
        position: absolute;
        box-shadow: var(--shadow-md);
        top: 1rem;
        left: calc(50% - 0.75rem);
        animation-name: list-item-in;
        scale: 0.95;
        z-index: 1;
        opacity: 1;
      }
    }
  }
}

.is-fullscreen {
  .header::after {
    @mixin screen xxl {
      margin-left: 0;
      margin-right: 0;
      width: 100vw;
      border-radius: 0;
      transition:
        border-radius 150ms ease-in-out,
        width 150ms ease-in-out,
        margin-left 150ms ease-in-out,
        margin-right 150ms ease-in-out;
    }
  }
  .spots-list {
    @mixin screen xxl {
      margin-left: 1vw;
      transition: margin-left 150ms ease-in-out;
    }
  }
  .map-view {
    @mixin screen xxl {
      border-radius: 0;
      padding-right: 0;
      padding-left: 1.5rem;
      transition:
        border-radius 150ms ease-in-out,
        padding-right 150ms ease-in-out,
        padding-left 150ms ease-in-out;
    }
  }
  .controls {
    @mixin screen xxl {
      padding-left: 1.75rem;
      transition: padding-left 150ms ease-in-out;
    }
  }
  .filter {
    @mixin screen xxl {
      margin-left: 2rem;
      margin-right: 0;
      transition:
        margin-left 150ms ease-in-out,
        margin-right 150ms ease-in-out;
    }
  }
  .progress-container {
    @mixin screen xxl {
      margin-left: calc(2vw - 0.75rem);
      transition: margin-left 150ms ease-in-out;
    }
  }
  .creator-tag {
    @mixin screen md {
      position: fixed;
      top: 1rem;
      right: 1rem;
    }
  }
  .spot-view {
    @mixin screen xxl {
      margin-left: 2.25rem;
      margin-right: 0;
      transition:
        margin-left 150ms ease-in-out,
        margin-right 150ms ease-in-out;
    }
  }
}

.has-open-spot {
  .controls {
    @mixin screen md {
      padding-left: 31rem;
      transition: padding-left 150ms ease-in-out;
    }
    @mixin screen xxl {
      padding-left: calc(((100vw - $layoutWide) / 2) + 31rem);
      transition: padding-left 150ms ease-in-out;
    }
  }

  .filter {
    @mixin screen md {
      margin-left: calc(((100vw - $layoutWide) / 2) + 31rem);
    }
  }
}

.has-open-spot.is-fullscreen {
  .controls {
    @mixin screen xxl {
      padding-left: 32.25rem;
      transition: padding-left 150ms ease-in-out;
    }
  }
  .filter {
    @mixin screen md {
      margin-left: 31rem;
      transition: margin-left 150ms ease-in-out;
    }
    @mixin screen xxl {
      margin-left: 32.5rem;
    }
  }
}

@keyframes list-item-in {
  from {
    transform: translate(-50%, 8rem);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@keyframes list-item-out {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(8rem);
    opacity: 0;
  }
}

.callout {
  padding: 0 !important;
  @mixin screen md {
    padding: 0 0.5rem 1rem !important;
  }
}

.auth-callout {
  background-image: url("/media/images/callout-sign-up.svg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  background-color: var(--color-rose-400);
  border-radius: 1rem;

  position: relative;
  padding: 1rem;
  color: var(--color-white);

  h5 {
    margin: 0;
    line-height: 100%;
    margin-bottom: 0.75rem;
    font-weight: 600 !important;
    span {
      font-weight: 700 !important;
    }
  }
  p {
    margin: 0 1.5rem 1rem 0;
    line-height: 120% !important;
  }
}

.premium-callout {
  padding: 1rem 1rem;
  border-radius: 1rem;
  overflow: hidden;
  background-image: url("/media/images/callout-premium.svg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  background-color: var(--color-indigo-600);

  .premium-callout-content {
    display: grid;
    color: var(--color-indigo-200);
    z-index: 2;
    position: relative;

    h5 {
      margin: 0;
      color: var(--color-slate-50);
      font-size: 23px;
      font-weight: 700;
      line-height: 1.05 !important;
      margin-bottom: 0.5rem;
    }
    p {
      color: var(--color-slate-300);
      margin: 0 1.5rem 0.75rem 0;
      line-height: 1.2 !important;
      font-weight: 400;
    }
  }

  .premium-callout-button-container {
    display: flex;
    gap: 1rem;
    align-items: center;
  }
}

.support-callout {
  background-image: url("/media/images/callout-support.svg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  background-color: var(--color-slate-800);
  padding: 1rem;
  border-radius: 1rem;

  .support-content {
    display: grid;
    color: var(--color-indigo-200);
    z-index: 2;
    position: relative;

    h5 {
      margin: 0;
      color: var(--color-slate-50);
      font-size: 23px;
      font-weight: 700;
      line-height: 1.05 !important;
      margin-bottom: 0.5rem;
    }
    p {
      color: var(--color-slate-300);
      margin: 0;
      font-size: 14px;
      line-height: 1.2 !important;
      font-weight: 400;
      max-width: 80%;
      margin-bottom: 1rem;
    }
  }

  .callout-support-graphic-left {
    z-index: 1;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    display: grid;
    img {
      grid-column: 1/-1;
      grid-row: 1/-1;
    }
  }

  .callout-support-graphic-right {
    z-index: 1;
    position: absolute;
    bottom: 0;
    right: 0;

    height: 100%;
    width: 50%;
    display: grid;
    img {
      place-self: end;
      grid-column: 1/-1;
      grid-row: 1/-1;
    }

    img:first-child {
      position: absolute;
      bottom: 0;
    }

    img:nth-child(2) {
      position: absolute;
      top: 0;
    }

    img:nth-child(3) {
      position: absolute;
      bottom: 0;
      height: 30%;
      right: 30px;
    }

    img:nth-child(4) {
      position: absolute;
      top: 0;
      height: 50%;
    }
    img:nth-child(5) {
      position: absolute;
      bottom: 30px;
      right: 90px;
      height: 20%;
    }
  }
}

.spots-list ul li.swipe-instruction {
  height: 100%;
  max-width: 100vw !important;
  border-radius: 0;
  color: white;
  text-align: center;
  display: flex;
  gap: 0.25rem;
  flex-direction: column;
  align-items: center;
  background: transparent;
  justify-content: end;
  padding: 1rem 1rem 2rem;
  @mixin screen md {
    display: none;
  }
}

.spots-list ul li.swipe-instruction.active {
  z-index: 3;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.75) 70.19%
  );
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
